<template>
  <div class="evaluation-page" :class="pageTheme">
    <div class="ev-bg" aria-hidden="true">
      <span class="ev-wash"></span>
      <span class="ev-grid"></span>
    </div>

    <!-- RA 10173 consent gate. Rendered before the form and blocking it: the
         Act requires consent before collection, so the fields cannot be
         reachable until this is answered. -->
    <div
      v-if="consentOpen"
      class="ev-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ev-consent-title"
    >
      <div class="ev-modal" ref="consentModal">
        <div class="ev-modal-head">
          <div class="ev-modal-icon"><i class="bi bi-shield-lock-fill"></i></div>
          <div>
            <h5 id="ev-consent-title">Data Privacy Consent</h5>
            <p>Republic Act No. 10173 — Data Privacy Act of 2012</p>
          </div>
        </div>

        <div class="ev-modal-body" tabindex="0">
          <p>
            The Commission on Higher Education Regional Office XIII (CHED
            Caraga) collects your personal information on this form. Before you
            continue, please read how it will be used.
          </p>

          <h6>What we collect</h6>
          <ul>
            <li><b>Your name and email address</b></li>
            <li><b>Age, sex, senior citizen and PWD status</b></li>
            <li><b>Your ratings and comments</b> about the activity</li>
            <li>
              <b>Technical details</b> — your IP address and the date and time
              you submitted
            </li>
          </ul>

          <h6>Why we collect it</h6>
          <ul>
            <li>To generate and send your e-certificate for this activity</li>
            <li>To evaluate this activity and improve future ones</li>
            <li>
              To report participation by sex, senior citizen and PWD status, as
              government reporting requires
            </li>
          </ul>

          <h6>How it is handled</h6>
          <p>
            Your ratings are reported as summaries and averages, not attributed
            to you by name. Records are kept in accordance with the CHED Records
            Retention Schedule and applicable government archiving regulations,
            and are not sold or shared for marketing.
          </p>

          <h6>Your rights as a data subject</h6>
          <p>Under the Data Privacy Act of 2012, you have the right to:</p>
          <ul>
            <li><b>Be informed</b> about how your data is processed</li>
            <li><b>Access</b> the personal data we hold about you</li>
            <li><b>Correct</b> inaccurate or incomplete data</li>
            <li><b>Object</b> to processing, or request <b>erasure or blocking</b></li>
            <li>
              <b>File a complaint</b> before the National Privacy Commission
            </li>
          </ul>
          <p>
            To exercise these rights, contact the CHED Caraga Data Protection
            Officer at
            <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a>.
          </p>

          <p class="ev-modal-note">
            Giving consent is voluntary. If you do not consent, you cannot submit
            this form and no e-certificate can be issued — you may still give
            your feedback to the organiser directly.
          </p>
        </div>

        <div class="ev-modal-foot">
          <label class="ev-consent-check">
            <input v-model="consentChecked" type="checkbox" />
            <span>
              I have read and understood this notice, and I consent to CHED
              Caraga collecting and processing my personal information for the
              purposes stated above.
            </span>
          </label>
          <div class="ev-modal-actions">
            <button type="button" class="ev-modal-decline" @click="declineConsent">
              I do not consent
            </button>
            <button
              type="button"
              class="ev-modal-agree"
              :disabled="!consentChecked"
              @click="acceptConsent"
            >
              <i class="bi bi-check2 mr-1"></i> Agree and continue
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Shown only when consent was refused. -->
    <div v-if="consentDeclined" class="ev-modal-backdrop">
      <div class="ev-modal ev-modal-slim">
        <div class="ev-modal-head">
          <div class="ev-modal-icon muted"><i class="bi bi-info-circle-fill"></i></div>
          <div>
            <h5>Consent not given</h5>
            <p>Nothing has been collected or submitted.</p>
          </div>
        </div>
        <div class="ev-modal-body">
          <p>
            Without your consent we cannot record your evaluation or issue an
            e-certificate. You can still share your feedback with the activity
            organiser directly, or email
            <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a>.
          </p>
          <p>
            Changed your mind? You can reopen the notice and continue.
          </p>
        </div>
        <div class="ev-modal-foot">
          <div class="ev-modal-actions">
            <button type="button" class="ev-modal-agree" @click="reopenConsent">
              Read the notice again
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="ev-shell">
      <div class="ev-brand">
        <img
          class="ev-logo"
          :src="logoSrc"
          :srcset="logoSrcset"
          sizes="96px"
          width="96"
          height="68"
          alt="UGNAY — CHEDRO Caraga Unified Governance Network"
          decoding="async"
        />
        <span class="ev-brand-label">
          <span class="ev-brand-dot"></span>
          UGNAY · CHED Caraga
        </span>
      </div>

      <div v-if="submitted" class="ev-card ev-state">
        <div class="state-icon success"><i class="bi bi-check-circle-fill"></i></div>
        <h5>Thank you for your feedback!</h5>
        <p>Your evaluation has been recorded.</p>
        <p class="ev-certname">
          <i class="bi bi-patch-check-fill"></i>
          E-certificate for <b>{{ submittedName }}</b>
        </p>
        <p class="ev-certmail">
          <i class="bi bi-envelope"></i>
          <!-- Future tense: submitting records the address, it does not send
               anything. Nothing is emailed at this point. -->
          Will be sent to <b>{{ submittedEmail }}</b>
        </p>
        <button type="button" class="ev-secondary" @click="startAnother">
          <i class="bi bi-plus-circle mr-2"></i>Submit another response
        </button>
      </div>

      <form v-else class="ev-card" novalidate @submit.prevent="submit">
        <div class="ev-header">
          <h4>Activity Evaluation</h4>
          <p v-if="activityTitle" class="ev-activity">{{ activityTitle }}</p>
          <p class="ev-desc">
            Please rate each factor below. Your answers help us improve future
            activities.
          </p>
        </div>

        <fieldset class="ev-modality">
          <legend class="ev-label">How was the activity conducted?</legend>
          <div
            class="modality-switch"
            :class="modality === 'on-venue' ? 'is-second' : 'is-first'"
            role="radiogroup"
            aria-label="How the activity was conducted"
          >
            <span class="modality-thumb" aria-hidden="true"></span>
            <button
              v-for="option in modalities"
              :key="option.id"
              type="button"
              role="radio"
              class="modality-option"
              :class="{ active: modality === option.id }"
              :aria-checked="modality === option.id ? 'true' : 'false'"
              @click="modality = option.id"
            >
              <i class="bi" :class="option.icon"></i>
              {{ option.label }}
            </button>
          </div>
          <p class="ev-hint">
            <i class="bi bi-info-circle"></i>
            {{
              modality === "on-venue"
                ? "Food and Venue are included for activities held on site."
                : "Food and Venue are skipped for virtual activities."
            }}
          </p>
        </fieldset>

        <div class="ev-section">
          <h6 class="ev-section-title">
            <i class="bi bi-person-badge"></i> About you
          </h6>

          <div class="ev-note">
            <i class="bi bi-patch-check-fill"></i>
            <div>
              <b>Your e-certificate</b>
              <p>
                The e-certificate for this activity is generated from the name
                you enter below and sent to your email address. Type your name
                exactly as it should appear on the certificate — including your
                middle initial and any suffix — and check both fields before you
                submit.
              </p>
            </div>
          </div>

          <div class="form-group">
            <label class="ev-label" for="ev-name">
              Full name <span class="req" aria-hidden="true">*</span>
            </label>
            <input
              id="ev-name"
              ref="name"
              v-model.trim="form.name"
              class="form-control ev-input"
              :class="{ 'is-invalid': errors.name }"
              :aria-invalid="errors.name ? 'true' : 'false'"
              aria-describedby="ev-name-help"
              maxlength="255"
              autocomplete="name"
              placeholder="e.g. Juan M. Dela Cruz Jr."
            />
            <p v-if="errors.name" class="ev-error" role="alert">{{ errors.name }}</p>
            <p id="ev-name-help" class="ev-help">
              As it should appear on your e-certificate.
            </p>
          </div>

          <div class="form-group">
            <label class="ev-label" for="ev-email">
              Email address <span class="req" aria-hidden="true">*</span>
            </label>
            <input
              id="ev-email"
              ref="email"
              v-model.trim="form.email"
              type="email"
              inputmode="email"
              autocomplete="email"
              spellcheck="false"
              class="form-control ev-input"
              :class="{ 'is-invalid': errors.email }"
              :aria-invalid="errors.email ? 'true' : 'false'"
              aria-describedby="ev-email-help"
              maxlength="255"
              placeholder="you@example.com"
            />
            <p v-if="errors.email" class="ev-error" role="alert">{{ errors.email }}</p>
            <p id="ev-email-help" class="ev-help">
              Where your e-certificate will be sent.
            </p>
          </div>

          <div class="form-group">
            <label class="ev-label" for="ev-age">
              Age <span class="req" aria-hidden="true">*</span>
            </label>
            <input
              id="ev-age"
              ref="age"
              v-model="form.age"
              type="number"
              inputmode="numeric"
              min="1"
              max="120"
              class="form-control ev-input ev-input-narrow"
              :class="{ 'is-invalid': errors.age }"
              :aria-invalid="errors.age ? 'true' : 'false'"
            />
            <p v-if="errors.age" class="ev-error" role="alert">{{ errors.age }}</p>
          </div>

          <div class="form-group">
            <span class="ev-label" id="ev-sex-label">
              Sex <span class="req" aria-hidden="true">*</span>
            </span>
            <div
              ref="sex"
              class="pill-group"
              role="radiogroup"
              aria-labelledby="ev-sex-label"
            >
              <button
                v-for="option in sexOptions"
                :key="option.value"
                type="button"
                role="radio"
                class="pill"
                :class="{ active: form.sex === option.value }"
                :aria-checked="form.sex === option.value ? 'true' : 'false'"
                @click="form.sex = option.value"
              >
                {{ option.label }}
              </button>
            </div>
            <p v-if="errors.sex" class="ev-error" role="alert">{{ errors.sex }}</p>
          </div>

          <div class="form-group">
            <span class="ev-label" id="ev-senior-label">
              Senior Citizen? <span class="req" aria-hidden="true">*</span>
            </span>
            <div
              ref="seniorCitizen"
              class="pill-group"
              role="radiogroup"
              aria-labelledby="ev-senior-label"
            >
              <button
                v-for="option in yesNoOptions"
                :key="String(option.value)"
                type="button"
                role="radio"
                class="pill"
                :class="{ active: form.seniorCitizen === option.value }"
                :aria-checked="form.seniorCitizen === option.value ? 'true' : 'false'"
                @click="form.seniorCitizen = option.value"
              >
                {{ option.label }}
              </button>
            </div>
            <p v-if="errors.seniorCitizen" class="ev-error" role="alert">
              {{ errors.seniorCitizen }}
            </p>
          </div>

          <div class="form-group mb-0">
            <span class="ev-label" id="ev-pwd-label">
              Person with Disability? <span class="req" aria-hidden="true">*</span>
            </span>
            <div
              ref="pwd"
              class="pill-group"
              role="radiogroup"
              aria-labelledby="ev-pwd-label"
            >
              <button
                v-for="option in yesNoOptions"
                :key="String(option.value)"
                type="button"
                role="radio"
                class="pill"
                :class="{ active: form.pwd === option.value }"
                :aria-checked="form.pwd === option.value ? 'true' : 'false'"
                @click="form.pwd = option.value"
              >
                {{ option.label }}
              </button>
            </div>
            <p v-if="errors.pwd" class="ev-error" role="alert">{{ errors.pwd }}</p>
          </div>
        </div>

        <div class="ev-section">
          <div class="ev-section-head">
            <h6 class="ev-section-title">
              <i class="bi bi-star-half"></i> Your ratings
            </h6>
            <span class="ev-progress" aria-live="polite">
              {{ ratedCount }} of {{ visibleFactors.length }} rated
            </span>
          </div>

          <div class="ev-progress-track" aria-hidden="true">
            <span class="ev-progress-fill" :style="{ width: progressWidth }"></span>
          </div>

          <ul class="ev-legend">
            <li v-for="step in ratingScale" :key="step.value">
              <b>{{ step.value }}</b> {{ step.label }}
            </li>
          </ul>

          <div
            v-for="(factor, idx) in visibleFactors"
            :key="factor.id"
            :ref="`factor-${factor.id}`"
            class="ev-factor"
            :class="{ 'has-error': errors.ratings && ratings[factor.id] == null }"
          >
            <span class="ev-label" :id="`ev-factor-${factor.id}`">
              <span class="q-num">{{ idx + 1 }}</span>
              {{ factor.label }}
              <span class="req" aria-hidden="true">*</span>
            </span>
            <div
              class="rating-grid"
              role="radiogroup"
              :aria-labelledby="`ev-factor-${factor.id}`"
            >
              <button
                v-for="step in ratingScale"
                :key="step.value"
                type="button"
                role="radio"
                class="rating-chip"
                :class="{ active: ratings[factor.id] === step.value }"
                :aria-checked="ratings[factor.id] === step.value ? 'true' : 'false'"
                :aria-label="`${step.value} — ${step.label}`"
                @click="setRating(factor.id, step.value)"
              >
                {{ step.value }}
              </button>
              <span class="rating-word" :class="{ shown: ratings[factor.id] != null }">
                {{ ratingWord(factor.id) }}
              </span>
            </div>
          </div>

          <p v-if="errors.ratings" class="ev-error" role="alert">{{ errors.ratings }}</p>
        </div>

        <div class="ev-section">
          <label class="ev-label" for="ev-comments">
            <i class="bi bi-chat-left-text mr-1"></i>
            Comments/Suggestions <span class="opt">(optional)</span>
          </label>
          <textarea
            id="ev-comments"
            v-model.trim="form.comments"
            class="form-control ev-input"
            rows="4"
            maxlength="2000"
            placeholder="What worked well? What should we do differently?"
          ></textarea>
          <p class="ev-count">{{ (form.comments || "").length }}/2000</p>
        </div>

        <button type="submit" class="ev-submit" :disabled="submitting">
          <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="bi bi-send-fill mr-2"></i>
          {{ submitting ? "Submitting..." : "Submit Evaluation" }}
        </button>
      </form>

      <p class="ev-footer">
        Your ratings are reported in summary; your name and email are used for
        your e-certificate ·
        <button type="button" class="ev-footer-link" @click="reopenConsent">
          Data privacy notice
        </button>
        · CHED Caraga
      </p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import toastr from "toastr";
import {
  MODALITIES,
  RATING_SCALE,
  factorsFor,
  ratingsFor,
} from "./factors";

/**
 * Bump this whenever the wording of the consent notice changes materially.
 * It is stored with each response so it stays possible to say which notice a
 * given participant actually agreed to.
 */
const PRIVACY_NOTICE_VERSION = "2026-08-21";

export default {
  name: "ActivityEvaluation",
  data() {
    return {
      modalities: MODALITIES,
      ratingScale: RATING_SCALE,
      sexOptions: [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
      ],
      yesNoOptions: [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
      ],
      modality: "on-venue",
      ratings: {},
      form: {
        name: "",
        email: "",
        age: "",
        sex: null,
        seniorCitizen: null,
        pwd: null,
        comments: "",
      },
      errors: {},
      consentOpen: true,
      consentChecked: false,
      consentGiven: false,
      consentDeclined: false,
      submitting: false,
      submitted: false,
      submittedName: "",
      submittedEmail: "",
    };
  },
  computed: {
    pageTheme() {
      return this.modality === "on-venue" ? "theme-venue" : "theme-virtual";
    },
    visibleFactors() {
      return factorsFor(this.modality);
    },
    ratedCount() {
      return this.visibleFactors.filter((f) => this.ratings[f.id] != null).length;
    },
    progressWidth() {
      const total = this.visibleFactors.length;
      return `${total ? (this.ratedCount / total) * 100 : 0}%`;
    },
    activityTitle() {
      return this.$route.query.title || null;
    },
    activityId() {
      return this.$route.query.activity_id || this.$route.query.activity || null;
    },
    // Same asset and BASE_URL resolution as components/brand/CcmisLogo.vue.
    logoSrc() {
      return `${process.env.BASE_URL}ugnay-logo-512.png`;
    },
    logoSrcset() {
      return `${this.logoSrc} 842w, ${process.env.BASE_URL}ugnay-logo@2x.png 1684w`;
    },
  },
  mounted() {
    document.title = "Activity Evaluation | CHED Caraga";
    this.focusConsent();
  },
  watch: {
    consentOpen(open) {
      if (open) this.focusConsent();
    },
  },
  methods: {
    focusConsent() {
      this.$nextTick(() => {
        const body =
          this.$refs.consentModal &&
          this.$refs.consentModal.querySelector(".ev-modal-body");
        if (body && body.focus) body.focus();
      });
    },
    acceptConsent() {
      if (!this.consentChecked) return;
      this.consentGiven = true;
      this.consentOpen = false;
      this.consentDeclined = false;
      this.$nextTick(() => {
        if (this.$refs.name && this.$refs.name.focus) this.$refs.name.focus();
      });
    },
    declineConsent() {
      this.consentGiven = false;
      this.consentOpen = false;
      this.consentDeclined = true;
    },
    reopenConsent() {
      this.consentDeclined = false;
      this.consentChecked = false;
      this.consentOpen = true;
    },
    setRating(factorId, value) {
      this.$set(this.ratings, factorId, value);
      if (this.errors.ratings && this.ratedCount === this.visibleFactors.length) {
        this.$delete(this.errors, "ratings");
      }
    },
    ratingWord(factorId) {
      const step = RATING_SCALE.find((s) => s.value === this.ratings[factorId]);
      return step ? step.label : "";
    },
    looksLikeEmail(value) {
      // Deliberately loose: one @, something either side, a dot in the domain.
      // A stricter pattern rejects addresses that are genuinely deliverable,
      // and only sending can really prove an address works.
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
    },
    validate() {
      const errors = {};
      if (!this.form.name) {
        errors.name = "Please enter your name — the e-certificate is issued to it.";
      }
      if (!this.form.email) {
        errors.email = "Please enter your email — the e-certificate is sent there.";
      } else if (!this.looksLikeEmail(this.form.email)) {
        errors.email = "Check this email address — it does not look complete.";
      }
      const age = Number(this.form.age);
      if (!this.form.age) errors.age = "Please enter your age.";
      else if (!Number.isInteger(age) || age < 1 || age > 120) {
        errors.age = "Enter an age between 1 and 120.";
      }
      if (!this.form.sex) errors.sex = "Please select your sex.";
      if (this.form.seniorCitizen === null) {
        errors.seniorCitizen = "Please answer this item.";
      }
      if (this.form.pwd === null) errors.pwd = "Please answer this item.";

      const unrated = this.visibleFactors.filter(
        (f) => this.ratings[f.id] == null
      );
      if (unrated.length) {
        errors.ratings = `Please rate all ${this.visibleFactors.length} factors — ${unrated.length} still unrated.`;
      }
      this.errors = errors;
      return { valid: !Object.keys(errors).length, firstUnrated: unrated[0] };
    },
    focusFirstProblem(firstUnrated) {
      const order = ["name", "email", "age", "sex", "seniorCitizen", "pwd"];
      const field = order.find((key) => this.errors[key]);
      if (field) {
        const el = this.$refs[field];
        const target = el && el.focus ? el : el && el.querySelector("button");
        if (target && target.focus) target.focus();
        else if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: "center" });
        }
        return;
      }
      if (!firstUnrated) return;
      const group = this.$refs[`factor-${firstUnrated.id}`];
      const node = Array.isArray(group) ? group[0] : group;
      if (node && node.scrollIntoView) {
        node.scrollIntoView({ block: "center" });
        const chip = node.querySelector(".rating-chip");
        if (chip) chip.focus();
      }
    },
    payload() {
      return {
        modality: this.modality,
        activity_id: this.activityId || undefined,
        respondent: {
          name: this.form.name,
          email: this.form.email,
          age: Number(this.form.age),
          sex: this.form.sex,
          senior_citizen: this.form.seniorCitizen,
          pwd: this.form.pwd,
        },
        ratings: ratingsFor(this.modality, this.ratings),
        comments: this.form.comments || null,
        consent: {
          agreed: true,
          notice_version: PRIVACY_NOTICE_VERSION,
        },
      };
    },
    async submit() {
      // The gate should make this unreachable; keeping it means a template
      // change can never turn into an unconsented submission.
      if (!this.consentGiven) {
        this.reopenConsent();
        return;
      }
      const { valid, firstUnrated } = this.validate();
      if (!valid) {
        this.$nextTick(() => this.focusFirstProblem(firstUnrated));
        return;
      }
      this.submitting = true;
      try {
        // /evaluation is public, so nothing on this page has talked to the API
        // before now and the visitor holds no XSRF-TOKEN cookie. The endpoint
        // still runs through Sanctum's stateful middleware, which answers a
        // tokenless POST with 419 "CSRF token mismatch" — mint the cookie
        // first, the same way Login.vue does.
        await axios.get("/sanctum/csrf-cookie");
        await axios.post("api/activity-evaluations", this.payload());
        this.submittedName = this.form.name;
        this.submittedEmail = this.form.email;
        this.submitted = true;
        document.title = "Thank You | CHED Caraga";
        window.scrollTo(0, 0);
      } catch (e) {
        const message = !e.response
          ? "Could not reach the server. Please check your internet connection and try again."
          : e.response.data?.message || "Failed to submit your evaluation.";
        toastr.error(message, "Error");
      } finally {
        this.submitting = false;
      }
    },
    startAnother() {
      // A single tablet at the registration desk is passed from one participant
      // to the next, so the page has to reset rather than remember a submission.
      this.ratings = {};
      this.form = {
        name: "",
        email: "",
        age: "",
        sex: null,
        seniorCitizen: null,
        pwd: null,
        comments: "",
      };
      this.errors = {};
      this.submitted = false;
      // A shared tablet passes to a new person, and consent is personal — the
      // next respondent has to be asked again rather than inheriting it.
      this.consentGiven = false;
      this.consentChecked = false;
      this.consentDeclined = false;
      this.consentOpen = true;
      document.title = "Activity Evaluation | CHED Caraga";
    },
  },
};
</script>

<style scoped>
/*
 * UGNAY theme, matching src/views/Portal/Apps.vue and the UGNAY portal itself:
 * #0b1524 base under a blue/gold wash, a masked grid, glass cards, gold as the
 * primary action. No font is declared — the app already loads Fira Sans
 * globally, and the other UGNAY-styled pages here inherit it too.
 */
.evaluation-page {
  --ink: #f4f7fb;
  --ink-muted: rgba(244, 247, 251, 0.7);
  /* 0.58, not lower: below that, placeholders and the "(optional)" tags
     drop under 4.5:1 against the glass card. */
  --ink-faint: rgba(244, 247, 251, 0.58);
  --gold: #e8a43a;
  --blue: #7eb8e8;
  --line: rgba(255, 255, 255, 0.1);
  --danger: #fecaca;

  position: relative;
  min-height: 100vh;
  padding: 2rem 1rem 3rem;
  color: var(--ink);
  background: #0b1524;
  overflow: hidden;
}

/* Accent follows the switch: blue for Virtual, gold for On Venue. */
.theme-virtual { --accent: var(--blue); --accent-ink: #0b1524; }
.theme-venue { --accent: var(--gold); --accent-ink: #1c1408; }

.ev-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.ev-wash {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 20% 0%, rgba(29, 78, 216, 0.35), transparent 60%),
    radial-gradient(ellipse 50% 40% at 90% 20%, rgba(232, 164, 58, 0.18), transparent 55%),
    linear-gradient(180deg, #0f1d36 0%, #0b1524 100%);
}

.ev-grid {
  position: absolute;
  inset: 0;
  opacity: 0.12;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, #000 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, #000 30%, transparent 75%);
}

.ev-shell {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.ev-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.ev-logo {
  display: block;
  width: 96px;
  height: auto;
  /* The mark is full-colour on transparent, so it only needs lifting off the
     navy — no plate or halo behind it. */
  filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.45));
}

.ev-brand-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--gold);
  text-transform: uppercase;
  letter-spacing: 0.14em;
}

.ev-brand-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--gold);
  box-shadow: 0 0 0 4px rgba(232, 164, 58, 0.18);
}

.ev-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 1.75rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
}

.ev-state {
  text-align: center;
  padding: 2.5rem 1.75rem;
  color: var(--ink-muted);
}

.state-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.75rem;
}

.state-icon.success {
  background: rgba(232, 164, 58, 0.16);
  color: var(--gold);
}

.ev-state h5 {
  color: var(--ink);
  font-weight: 700;
}

.ev-secondary {
  margin-top: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 0.6rem 1.1rem;
  min-height: 44px;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--ink);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.ev-secondary:hover {
  border-color: rgba(232, 164, 58, 0.55);
  background: rgba(232, 164, 58, 0.1);
  color: var(--gold);
}

.ev-header { margin-bottom: 1.5rem; }

.ev-header h4 {
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--ink);
  margin-bottom: 0.35rem;
}

.ev-activity {
  font-weight: 600;
  color: var(--blue);
  font-size: 0.95rem;
  margin-bottom: 0.35rem;
}

.ev-desc {
  color: var(--ink-muted);
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 0;
}

.ev-modality {
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: rgba(0, 0, 0, 0.18);
}

.ev-modality legend {
  float: none;
  width: auto;
  font-size: 0.88rem;
  margin-bottom: 0.6rem;
}

.modality-switch {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  padding: 0.3rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}

.modality-thumb {
  position: absolute;
  top: 0.3rem;
  bottom: 0.3rem;
  left: 0.3rem;
  width: calc(50% - 0.425rem);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid var(--accent);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  transition: transform 0.22s ease, border-color 0.22s ease;
}

.modality-switch.is-second .modality-thumb {
  transform: translateX(calc(100% + 0.25rem));
}

.modality-option {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 44px;
  border: none;
  background: transparent;
  border-radius: 11px;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ink-muted);
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.18s ease;
}

.modality-option.active { color: var(--accent); }

.ev-note {
  display: flex;
  gap: 0.6rem;
  padding: 0.85rem 0.9rem;
  margin-bottom: 1.25rem;
  border: 1px solid rgba(232, 164, 58, 0.35);
  border-radius: 12px;
  background: rgba(232, 164, 58, 0.08);
}

.ev-note > i {
  color: var(--gold);
  font-size: 1rem;
  line-height: 1.4;
}

.ev-note b {
  display: block;
  font-size: 0.84rem;
  color: var(--ink);
  margin-bottom: 0.15rem;
}

.ev-note p {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--ink-muted);
}

.ev-help {
  font-size: 0.75rem;
  color: var(--ink-faint);
  margin: 0.35rem 0 0;
}

.ev-certname {
  /* flex + fit-content, not inline-flex: inline-level would let the
     "Submit another response" button ride up onto the same line. */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: fit-content;
  max-width: 100%;
  margin: 0.9rem auto 0;
  padding: 0.5rem 0.85rem;
  border: 1px solid rgba(232, 164, 58, 0.35);
  border-radius: 999px;
  background: rgba(232, 164, 58, 0.08);
  font-size: 0.82rem;
  color: var(--ink-muted);
}

.ev-certname i { color: var(--gold); }
.ev-certname b { color: var(--ink); }

.ev-certmail {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin: 0.45rem 0 0;
  font-size: 0.8rem;
  color: var(--ink-muted);
  word-break: break-all;
}

.ev-certmail i { color: var(--blue); }
.ev-certmail b { color: var(--ink); font-weight: 600; }

.ev-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--ink-muted);
  margin: 0.65rem 0 0;
}

.ev-section {
  padding-top: 1.35rem;
  margin-bottom: 1.35rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.ev-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.ev-section-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--gold);
  margin-bottom: 1rem;
}

.ev-section-head .ev-section-title { margin-bottom: 0.5rem; }

.ev-progress {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--ink-muted);
  margin-bottom: 0.5rem;
}

.ev-progress-track {
  height: 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.35);
  overflow: hidden;
  margin-bottom: 1rem;
}

.ev-progress-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--accent);
  transition: width 0.25s ease, background 0.22s ease;
}

.ev-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.9rem;
  list-style: none;
  padding: 0.6rem 0.75rem;
  margin: 0 0 1.25rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  font-size: 0.75rem;
  color: var(--ink-muted);
}

.ev-legend b { color: var(--ink); margin-right: 0.2rem; }

.ev-label {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 0.55rem;
}

.ev-label .opt { font-weight: 400; color: var(--ink-faint); font-size: 0.8rem; }

.q-num {
  width: 22px;
  height: 22px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--ink-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.req { color: var(--gold); }

.ev-input {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(0, 0, 0, 0.25);
  color: var(--ink);
  font-size: 0.95rem;
  min-height: 44px;
}

.ev-input::placeholder { color: var(--ink-faint); }

.ev-input:focus {
  background: rgba(0, 0, 0, 0.32);
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(232, 164, 58, 0.2);
  color: var(--ink);
}

.theme-virtual .ev-input:focus { box-shadow: 0 0 0 3px rgba(126, 184, 232, 0.22); }

/* Chrome paints autofilled fields white, which would break the dark card. */
.ev-input:-webkit-autofill,
.ev-input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--ink);
  -webkit-box-shadow: 0 0 0 1000px #142440 inset;
}

.ev-input-narrow { max-width: 140px; }

.ev-input.is-invalid {
  border-color: rgba(248, 113, 113, 0.7);
  background-image: none;
}

.ev-error {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--danger);
  margin: 0.4rem 0 0;
}

.ev-count {
  font-size: 0.72rem;
  color: var(--ink-faint);
  text-align: right;
  margin: 0.35rem 0 0;
}

.pill-group { display: flex; flex-wrap: wrap; gap: 0.5rem; }

.pill {
  min-width: 88px;
  min-height: 44px;
  padding: 0 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--ink);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.pill:hover {
  border-color: var(--accent);
  background: rgba(255, 255, 255, 0.09);
}

.pill.active {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--accent-ink);
}

.ev-factor {
  padding: 0.85rem;
  border: 1px solid transparent;
  border-radius: 14px;
  margin-bottom: 0.6rem;
  transition: background 0.15s ease;
}

.ev-factor:hover { background: rgba(255, 255, 255, 0.035); }

.ev-factor.has-error {
  border-color: rgba(248, 113, 113, 0.45);
  background: rgba(248, 113, 113, 0.07);
}

.rating-grid {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.rating-chip {
  width: 46px;
  height: 46px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--ink);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
}

.rating-chip:hover {
  border-color: var(--accent);
  background: rgba(255, 255, 255, 0.09);
}

.rating-chip.active {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--accent-ink);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}

.rating-word {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--accent);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.rating-word.shown { opacity: 1; }

.modality-option:focus-visible,
.pill:focus-visible,
.rating-chip:focus-visible,
.ev-secondary:focus-visible,
.ev-submit:focus-visible {
  outline: 3px solid rgba(232, 164, 58, 0.55);
  outline-offset: 2px;
}

/* Gold in both modes: the primary action is UGNAY's, not the switch's. */
.ev-submit {
  width: 100%;
  border: 0;
  border-radius: 10px;
  padding: 0.85rem;
  min-height: 48px;
  font-weight: 700;
  font-size: 0.98rem;
  color: #1c1408;
  background: var(--gold);
  cursor: pointer;
  transition: opacity 0.15s ease, filter 0.15s ease;
}

.ev-submit:hover { filter: brightness(1.06); }
.ev-submit:disabled { opacity: 0.7; cursor: wait; }

/* ---- RA 10173 consent gate ---- */

.ev-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  /* Strong scrim: the form behind must read as unavailable, not merely dimmed. */
  background: rgba(4, 10, 20, 0.72);
  backdrop-filter: blur(4px);
}

.ev-modal {
  width: 100%;
  max-width: 640px;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  background: #0f1d36;
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
  overflow: hidden;
}

.ev-modal-slim { max-width: 460px; }

.ev-modal-head {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.15rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.ev-modal-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: var(--gold);
  background: rgba(232, 164, 58, 0.14);
}

.ev-modal-icon.muted {
  color: var(--blue);
  background: rgba(126, 184, 232, 0.14);
}

.ev-modal-head h5 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ink);
}

.ev-modal-head p {
  margin: 0.15rem 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--gold);
  letter-spacing: 0.02em;
}

.ev-modal-body {
  padding: 1.15rem 1.25rem;
  overflow-y: auto;
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--ink-muted);
}

.ev-modal-body h6 {
  margin: 1.15rem 0 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--gold);
}

.ev-modal-body h6:first-of-type { margin-top: 1rem; }
.ev-modal-body p { margin: 0 0 0.6rem; }
.ev-modal-body ul { margin: 0 0 0.6rem; padding-left: 1.1rem; }
.ev-modal-body li { margin-bottom: 0.25rem; }
.ev-modal-body b { color: var(--ink); font-weight: 600; }
.ev-modal-body a { color: var(--blue); text-decoration: underline; }

.ev-modal-note {
  margin-top: 1rem !important;
  padding: 0.7rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 3px solid var(--gold);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.8rem;
}

.ev-modal-foot {
  padding: 1rem 1.25rem 1.15rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
}

.ev-consent-check {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--ink);
  cursor: pointer;
}

.ev-consent-check input {
  width: 20px;
  height: 20px;
  margin-top: 0.1rem;
  flex-shrink: 0;
  accent-color: #e8a43a;
  cursor: pointer;
}

.ev-modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
}

.ev-modal-decline,
.ev-modal-agree {
  min-height: 44px;
  padding: 0 1.1rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
}

.ev-modal-decline {
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: var(--ink-muted);
}

.ev-modal-decline:hover { color: var(--ink); border-color: rgba(255, 255, 255, 0.32); }

.ev-modal-agree {
  border: 0;
  background: var(--gold);
  color: #1c1408;
}

.ev-modal-agree:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ev-modal-body:focus-visible,
.ev-modal-decline:focus-visible,
.ev-modal-agree:focus-visible,
.ev-consent-check input:focus-visible,
.ev-footer-link:focus-visible {
  outline: 3px solid rgba(232, 164, 58, 0.55);
  outline-offset: 2px;
}

@media (max-width: 575px) {
  .ev-modal-actions { flex-direction: column-reverse; }
  .ev-modal-decline,
  .ev-modal-agree { width: 100%; }
}

.ev-footer-link {
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: var(--blue);
  text-decoration: underline;
  cursor: pointer;
}

.ev-footer {
  text-align: center;
  font-size: 0.72rem;
  color: var(--ink-faint);
  margin: 1rem 0 0;
}

@media (max-width: 575px) {
  .ev-card { padding: 1.25rem; border-radius: 16px; }
  .rating-chip { width: 44px; height: 44px; }
  .pill { flex: 1 1 auto; }
}

@media (prefers-reduced-motion: reduce) {
  .modality-thumb,
  .ev-progress-fill,
  .rating-word,
  .rating-chip,
  .pill,
  .modality-option,
  .ev-submit {
    transition: none;
  }
}
</style>
